// src/communaute/MessagerieModals.jsx
//
// <NouvelleConversationModal />  — message privé ou création de groupe (avec photo)
// <DetailsConversationModal />   — infos, membres, rôles, photo, muet, archive, quitter

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  ArchiveRestore,
  BadgeCheck,
  Bell,
  BellOff,
  Camera,
  Check,
  LogOut,
  MoreVertical,
  Pencil,
  Search,
  ShieldCheck,
  ShieldOff,
  Trash2,
  User,
  UserMinus,
  UserPlus,
  Users,
  Ban,
} from 'lucide-react';

import {
  ajouterMembres,
  archiverConversation,
  basculerMuet,
  bloquerUtilisateur,
  changerRole,
  creerGroupe,
  getAmis,
  getConversationDetails,
  modifierGroupe,
  ouvrirPrive,
  quitterConversation,
  retirerMembre,
} from './api';
import { Avatar, Chargement, Modal, Spinner, Vide, btnPrimaire, champ, confirmer, toast } from './ui';

// ══════════════════════════════════════════════════════
//  LIGNE CONTACT SÉLECTIONNABLE
// ══════════════════════════════════════════════════════
function LigneContact({ ami, selection, onClick }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-gray-100">
      <Avatar src={ami.photo} taille={42} />
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate text-[15px] font-semibold text-gray-900">{ami.nom}</span>
        {ami.isAbonne && <BadgeCheck size={15} className="shrink-0 text-[#9B59B6]" />}
      </span>
      {selection !== undefined && (
        <span
          className={`flex h-[22px] w-[22px] items-center justify-center rounded-md border-2 ${selection ? 'border-[#7C3AED] bg-[#7C3AED]' : 'border-gray-300'
            }`}
        >
          {selection && <Check size={14} className="text-white" strokeWidth={3} />}
        </span>
      )}
    </button>
  );
}

function useAmis(ouvert, exclure = []) {
  const [saisie, setSaisie] = useState('');
  const [amis, setAmis] = useState([]);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    if (!ouvert) return undefined;
    let annule = false;
    const t = setTimeout(async () => {
      setChargement(true);
      const l = await getAmis(saisie);
      if (annule) return;
      setAmis(l.filter((a) => !exclure.includes(a.id)));
      setChargement(false);
    }, 300);
    return () => {
      annule = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, saisie]);

  return { saisie, setSaisie, amis, chargement };
}

// ══════════════════════════════════════════════════════
//  NOUVELLE CONVERSATION
// ══════════════════════════════════════════════════════
export function NouvelleConversationModal({ ouvert, onFermer, onOuverte }) {
  const [modeGroupe, setModeGroupe] = useState(false);
  const [nom, setNom] = useState('');
  const [photo, setPhoto] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const inputPhoto = useRef(null);
  const { saisie, setSaisie, amis, chargement } = useAmis(ouvert);

  useEffect(() => {
    if (ouvert) return;
    setModeGroupe(false);
    setNom('');
    setPhoto(null);
    setSelection(new Set());
    setSaisie('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert]);

  const apercu = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => apercu && URL.revokeObjectURL(apercu), [apercu]);

  const basculer = (id) =>
    setSelection((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const prive = async (ami) => {
    if (busy) return;
    setBusy(true);
    const c = await ouvrirPrive(ami.id);
    setBusy(false);
    if (!c) return toast("Impossible d'ouvrir la conversation");
    onOuverte(c);
  };

  const creer = async () => {
    if (!nom.trim()) return toast('Donnez un nom au groupe');
    if (selection.size === 0) return toast('Sélectionnez au moins un contact');
    setBusy(true);
    const c = await creerGroupe(nom.trim(), [...selection], photo);
    setBusy(false);
    if (!c) return toast('Impossible de créer le groupe');
    onOuverte(c);
  };

  return (
    <Modal
      ouvert={ouvert}
      onFermer={onFermer}
      titre={modeGroupe ? 'Nouveau groupe' : 'Nouveau message'}
      taille="md"
      pied={
        modeGroupe && (
          <button onClick={creer} disabled={busy} className={`${btnPrimaire} w-full`}>
            {busy ? (
              <Spinner taille={18} className="!text-white" />
            ) : (
              `Créer le groupe${selection.size ? ` (${selection.size})` : ''}`
            )}
          </button>
        )
      }
    >
      <div className="flex gap-2 px-5 pt-4">
        {[
          [false, User, 'Message privé'],
          [true, Users, 'Groupe'],
        ].map(([val, Icone, label]) => (
          <button
            key={label}
            onClick={() => {
              setModeGroupe(val);
              setSelection(new Set());
            }}
            className={`flex flex-1 items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold ${modeGroupe === val ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            <Icone size={16} /> {label}
          </button>
        ))}
      </div>

      {modeGroupe && (
        <div className="flex items-center gap-4 px-5 pt-4">
          <button
            onClick={() => inputPhoto.current?.click()}
            className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 border-[#9B59B6] bg-gray-200"
            title="Photo du groupe"
          >
            {apercu ? (
              <img src={apercu} alt="" className="h-full w-full object-cover" />
            ) : (
              <Users size={28} className="mx-auto text-gray-500" />
            )}
            <span className="absolute bottom-0 right-0 rounded-full border-2 border-white bg-[#7C3AED] p-1">
              <Camera size={11} className="text-white" />
            </span>
          </button>
          <input
            ref={inputPhoto}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              setPhoto(e.target.files?.[0] || null);
              e.target.value = '';
            }}
          />
          <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom du groupe" className={champ} />
        </div>
      )}

      <div className="relative px-5 pb-2 pt-4">
        <Search size={18} className="pointer-events-none absolute left-9 top-1/2 mt-1 -translate-y-1/2 text-gray-400" />
        <input
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Rechercher un contact"
          className={`${champ} rounded-full pl-11`}
        />
      </div>

      {chargement ? (
        <Chargement />
      ) : amis.length === 0 ? (
        <Vide icone={Users} titre="Aucun contact pour le moment" sous="Abonnez-vous à des personnes pour pouvoir leur écrire" />
      ) : (
        <div className="pb-3">
          {amis.map((a) => (
            <LigneContact
              key={a.id}
              ami={a}
              selection={modeGroupe ? selection.has(a.id) : undefined}
              onClick={() => (modeGroupe ? basculer(a.id) : prive(a))}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

// ══════════════════════════════════════════════════════
//  DÉTAILS D'UNE CONVERSATION
// ══════════════════════════════════════════════════════
export function DetailsConversationModal({ conversation, ouvert, onFermer, onChange, onQuittee }) {
  const [conv, setConv] = useState(conversation);
  const [chargement, setChargement] = useState(true);
  const [busy, setBusy] = useState(false);
  const [menuMembre, setMenuMembre] = useState(null);
  const [ajout, setAjout] = useState(false);
  const [selection, setSelection] = useState(new Set());
  const inputPhoto = useRef(null);

  const charger = async () => {
    const c = await getConversationDetails(conversation.id);
    setConv(c || conversation);
    setChargement(false);
  };

  useEffect(() => {
    if (!ouvert) return;
    setChargement(true);
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, conversation.id]);

  const apresAction = async (ok, msgOk, msgKo = 'Échec') => {
    toast(ok ? msgOk : msgKo, ok);
    if (ok) {
      await charger();
      onChange?.();
    }
  };

  const admin = conv.estGroupe && conv.suisAdmin;
  const idsMembres = (conv.membres || []).map((m) => m.id);
  const amisDispo = useAmis(ajout, idsMembres);

  const renommer = async () => {
    const nom = window.prompt('Nouveau nom du groupe', conv.titre);
    if (!nom?.trim() || nom.trim() === conv.titre) return;
    apresAction(await modifierGroupe(conv.id, { nom }), 'Groupe renommé');
  };

  const changerPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    const ok = await modifierGroupe(conv.id, { photo: file });
    setBusy(false);
    apresAction(ok, 'Photo mise à jour', "Échec de l'envoi");
  };

  const supprimerPhoto = async () => {
    setBusy(true);
    const ok = await modifierGroupe(conv.id, { supprimerPhoto: true });
    setBusy(false);
    apresAction(ok, 'Photo supprimée');
  };

  const valider = async () => {
    if (!selection.size) return;
    const r = await ajouterMembres(conv.id, [...selection]);
    setAjout(false);
    setSelection(new Set());
    apresAction(r.succes, r.message || 'Membres ajoutés', r.message || 'Échec');
  };

  const actionMembre = async (type, m) => {
    setMenuMembre(null);
    if (type === 'role') {
      const role = m.estAdmin ? 'membre' : 'admin';
      apresAction(
        await changerRole(conv.id, m.id, role),
        role === 'admin' ? `${m.nom} est maintenant admin` : `${m.nom} n'est plus admin`,
        'Impossible de changer le rôle',
      );
    } else if (type === 'retirer') {
      if (!(await confirmer({ titre: `Retirer ${m.nom} ?`, bouton: 'Retirer' }))) return;
      apresAction(await retirerMembre(conv.id, m.id), 'Membre retiré');
    } else if (type === 'bloquer') {
      if (
        !(await confirmer({
          titre: `Bloquer ${m.nom} ?`,
          message: 'Vos abonnements mutuels seront supprimés et vous ne pourrez plus échanger de messages.',
          bouton: 'Bloquer',
        }))
      )
        return;
      apresAction(await bloquerUtilisateur(m.id), 'Utilisateur bloqué');
    }
  };

  const basculerMuetConv = async () => apresAction(await basculerMuet(conv.id, !conv.muet), conv.muet ? 'Notifications activées' : 'Notifications coupées');
  const basculerArchive = async () => apresAction(await archiverConversation(conv.id, !conv.archive), conv.archive ? 'Désarchivée' : 'Archivée');

  const quitter = async () => {
    const ok = await confirmer({
      titre: conv.estGroupe ? 'Quitter le groupe ?' : 'Supprimer la conversation ?',
      message: 'Vous ne pourrez plus voir les messages de cette conversation.',
      bouton: conv.estGroupe ? 'Quitter' : 'Supprimer',
    });
    if (!ok) return;
    if (await quitterConversation(conv.id)) {
      toast('Conversation quittée', true);
      onQuittee?.(conv.id);
    } else toast('Échec');
  };

  const ligneAction = (Icone, label, onClick, danger = false) => (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-4 px-5 py-3 text-left text-[15px] hover:bg-gray-100 ${danger ? 'text-red-600' : 'text-gray-900'
        }`}
    >
      <Icone size={20} className={danger ? '' : 'text-gray-500'} /> {label}
    </button>
  );

  return (
    <Modal ouvert={ouvert} onFermer={onFermer} titre={conv.estGroupe ? 'Infos du groupe' : 'Infos'} taille="md">
      {chargement ? (
        <Chargement />
      ) : ajout ? (
        // ── Sous-vue : ajouter des membres ──
        <div>
          <div className="flex items-center justify-between px-5 py-3">
            <button onClick={() => setAjout(false)} className="text-sm text-gray-500 hover:text-gray-900">
              ← Retour
            </button>
            <button onClick={valider} disabled={!selection.size} className={btnPrimaire}>
              Ajouter{selection.size ? ` (${selection.size})` : ''}
            </button>
          </div>
          {amisDispo.chargement ? (
            <Chargement />
          ) : amisDispo.amis.length === 0 ? (
            <Vide icone={Users} titre="Tous vos contacts sont déjà dans le groupe" />
          ) : (
            amisDispo.amis.map((a) => (
              <LigneContact
                key={a.id}
                ami={a}
                selection={selection.has(a.id)}
                onClick={() =>
                  setSelection((s) => {
                    const n = new Set(s);
                    n.has(a.id) ? n.delete(a.id) : n.add(a.id);
                    return n;
                  })
                }
              />
            ))
          )}
        </div>
      ) : (
        <div className="pb-4">
          {/* En-tête */}
          <div className="flex flex-col items-center px-5 pb-4 pt-6">
            <button
              disabled={!admin || busy}
              onClick={() => inputPhoto.current?.click()}
              className="relative"
              title={admin ? 'Changer la photo' : undefined}
            >
              <Avatar src={conv.photo} taille={88} icone={conv.estGroupe ? Users : User} />
              {admin && (
                <span className="absolute bottom-0 right-0 rounded-full border-2 border-white bg-[#7C3AED] p-1.5">
                  {busy ? <Spinner taille={13} className="!text-white" /> : <Camera size={13} className="text-white" />}
                </span>
              )}
            </button>
            <input ref={inputPhoto} type="file" accept="image/*" hidden onChange={changerPhoto} />
            {admin && conv.photo && (
              <button onClick={supprimerPhoto} className="mt-2 text-xs text-red-600 hover:underline">
                Supprimer la photo
              </button>
            )}
            <h4 className="mt-3 text-center text-xl font-bold text-gray-900">{conv.titre}</h4>
            <p className="mt-1 text-[13px] text-gray-500">
              {conv.estGroupe ? `${conv.nbrParticipants} participants` : 'Discussion privée'}
            </p>
          </div>

          {/* Actions */}
          {admin && ligneAction(Pencil, 'Renommer le groupe', renommer)}
          {admin && ligneAction(UserPlus, 'Ajouter des membres', () => setAjout(true))}
          {ligneAction(conv.muet ? Bell : BellOff, conv.muet ? 'Réactiver les notifs' : 'Couper les notifs', basculerMuetConv)}
          {ligneAction(conv.archive ? ArchiveRestore : Archive, conv.archive ? 'Désarchiver' : 'Archiver', basculerArchive)}

          {/* Membres */}
          {conv.membres?.length > 0 && (
            <div className="mt-4">
              <p className="flex items-center gap-2 px-5 pb-2 text-[15px] font-bold text-gray-900">
                <Users size={17} className="text-[#9B59B6]" /> Membres ({conv.membres.length})
              </p>
              {conv.membres.map((m) => (
                <div key={m.id} className="relative flex items-center gap-3 px-5 py-2">
                  <Avatar src={m.photo} taille={40} />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate text-[15px] font-semibold text-gray-900">
                      {m.estMoi ? 'Vous' : m.nom}
                      {m.isAbonne && <BadgeCheck size={15} className="text-[#9B59B6]" />}
                    </p>
                    {m.estAdmin && <p className="text-xs text-[#9B59B6]">Administrateur</p>}
                  </div>
                  {admin && !m.estMoi && (
                    <button
                      onClick={() => setMenuMembre(menuMembre === m.id ? null : m.id)}
                      className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100"
                    >
                      <MoreVertical size={18} />
                    </button>
                  )}
                  {menuMembre === m.id && (
                    <div className="absolute right-5 top-11 z-10 w-60 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
                      <button onClick={() => actionMembre('role', m)} className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-900 hover:bg-gray-100">
                        {m.estAdmin ? <ShieldOff size={16} className="text-[#9B59B6]" /> : <ShieldCheck size={16} className="text-[#9B59B6]" />}
                        {m.estAdmin ? 'Retirer les droits admin' : 'Nommer administrateur'}
                      </button>
                      <button onClick={() => actionMembre('retirer', m)} className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-orange-600 hover:bg-gray-100">
                        <UserMinus size={16} /> Retirer du groupe
                      </button>
                      <button onClick={() => actionMembre('bloquer', m)} className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-gray-100">
                        <Ban size={16} /> Bloquer
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 border-t border-gray-200 pt-2">
            {ligneAction(conv.estGroupe ? LogOut : Trash2, conv.estGroupe ? 'Quitter le groupe' : 'Supprimer la conversation', quitter, true)}
          </div>
        </div>
      )}
    </Modal>
  );
}